// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    PayPalScriptProvider,
} from '@paypal/react-paypal-js'
import {
    Elements,
} from '@stripe/react-stripe-js'
import axios from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    SelectListInputSite,
    TextInputSite,
} from 'components'

// data
import {
    api_url_portfolio_site_select_country,
    api_url_shipping_cost_form_cost_calculation_site,
    api_url_portfolio_site_select_country_shipping_country,
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
    reduxSalesShippingCostSite,
} from 'data'

// pages
import {
    MondialRelayWidgetSite,
    PaypalCheckoutSite,
    StripeCheckoutSite,
} from 'pages'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
} from 'services'

// main
export const PortfolioCheckoutSite: React.FC = () => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxSalesSite = useSelector((state: defaultReduxState) => state.reduxSalesSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    const deviceType = reduxModalSite.deviceType

    useEffect(() => {
        if (reduxSalesSite.shippingTotal === undefined) {
            setShipping_info({
                ...shipping_info,
                country: '',
            })
        }
    }, [
        reduxSalesSite.shippingTotal,
    ])

    const errorsFields = {
        billing_info: {
            address: '',
            city: '',
            country: '',
            first_name: '',
            last_name: '',
            zip_code: '',
        },
        contact_info: {
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
        },
        shipping_info: {
            address: '',
            city: '',
            country: '',
            zip_code: '',
        },
    }

    const [billing_info, setBilling_info] = useState<any>(reduxSalesSite.billing_info)
    const [contact_info, setContact_info] = useState<any>(reduxSalesSite.contact_info)
    const [errors, setErrors] = useState<any>(errorsFields)
    const [hasError, setHasError] = useState(false)
    const [mainErrors, setMainErrors] = useState<any>()
    const [shipping_info, setShipping_info] = useState<any>({
        ...reduxSalesSite.shipping_info,
        country: '', // Ensure shipping country is not pre-populated
    })
    const [shippingType, setShippingType] = useState<any>('shipping')
    const [otherShippingCost, setOtherShippingCost] = useState<{
        id: number,
        agent: string,
        cost: number,
        service: string,
    }[] | undefined>()
    const [shipCostError, setShipCostError] = useState<string>('')
    const [customFields, setCustomFields] = useState<{ [key: string]: any }>({})
    const [customFieldErrors, setCustomFieldErrors] = useState<{ [key: string]: string }>({})
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
    const [termsError, setTermsError] = useState<string>('')

    // Dynamic custom fields configuration for different portfolios
    const customFieldsConfig = {
        696: {
            contactFields: [
                { name: 'company_name', label: "Nom de l'entreprise (facultatif)", type: 'text', required: false },
                { name: 'order_note', label: 'Note de commande (facultatif)', type: 'text', required: false },
                { name: 'newsletter', label: 'Je veux recevoir la newsletter de TOHU BOHU', type: 'checkbox', required: false },
            ],
            termsRequired: true,
            termsLabel: "J'ai lu et j'accepte les conditions générales de vente",
            // Note: Phone number is also required for this portfolio
        }
        // Add more portfolio configurations here as needed
        // Example for another portfolio:
        // 123: {
        //     contactFields: [
        //         { name: 'business_type', label: 'Type de business', type: 'select', required: true, options: ['B2B', 'B2C'] },
        //         { name: 'marketing_consent', label: 'Consentement marketing', type: 'checkbox', required: true }
        //     ],
        //     termsRequired: true,
        //     termsLabel: "J'accepte les conditions d'utilisation"
        // }
    }

    const currentPortfolioConfig = customFieldsConfig[reduxCacheSite.portfolio?.id as keyof typeof customFieldsConfig]

    // const hasMondialRelay = false  // TODO dynamic
    const hasMondialRelay = reduxCacheSite.portfolio?.id === 654  // TODO dynamic

    function handleInputChange(event: any, group: any) {
        try {
            if (group === 'billing_info') {
                setBilling_info({
                    ...billing_info,
                    [event.name]: event.value,
                })
                setErrors({
                    ...errors,
                    billing_info: {
                        ...errors.billing_info,
                        [event.name]: '',
                    }
                })
            } else if (group === 'contact_info') {
                setContact_info({
                    ...contact_info,
                    [event.name]: event.value,
                })
                setErrors({
                    ...errors,
                    contact_info: {
                        ...errors.contact_info,
                        [event.name]: '',
                    }
                })
                // Clear phone error specifically for portfolio 696
                if (event.name === 'phone' && reduxCacheSite.portfolio?.id === 696) {
                    setErrors((prev: any) => ({
                        ...prev,
                        contact_info: {
                            ...prev.contact_info,
                            phone: ''
                        }
                    }))
                }
            } else if (group === 'shipping_info') {
                setShipping_info({
                    ...shipping_info,
                    [event.name]: event.value,
                })
                setErrors({
                    ...errors,
                    shipping_info: {
                        ...errors.shipping_info,
                        [event.name]: '',
                    }
                })
                setShipCostError('')
                if (reduxCacheSite.portfolio?.id === 654) {
                    if (event.name === 'city' && event.value && shipping_info.country && shipping_info.zip_code) {
                        getShippingCost(shipping_info.country.id, undefined, event.value, shipping_info.zip_code)
                    }
                    if (event.name === 'zip_code' && event.value && shipping_info.city && shipping_info.country) {
                        getShippingCost(shipping_info.country.id, undefined, shipping_info.city, event.value)
                    }
                    if (event.name === 'country' && event.value && shipping_info.city && shipping_info.zip_code) {
                        getShippingCost(event.value.id, undefined, shipping_info.city, shipping_info.zip_code)
                    }
                } else if (event.name === 'country' && event.value && !noShiping) {
                    getShippingCost(event.value.id)
                }
            }
            setHasError(false)
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'PortfolioCheckoutSite',
                'handleInputChange',
            ))
        }
    }

    function getShippingCost(countryId: number, shippingService?: number, city?: string, zipCode?: string) {
        try {
            const formData: any = new FormData()
            formData.append('country', countryId)
            formData.append('service', shippingService || reduxCacheSite.portfolio?.shipping_services?.join(','))
            formData.append('sales_lines', JSON.stringify(reduxSalesSite.salesLines))
            if (reduxModalSite.currencyId) {
                formData.append('currency_id', reduxModalSite.currencyId)
            }
            if (reduxCacheSite.portfolio?.id === 654) {
                formData.append('city', city)
                formData.append('portfolio_id', reduxCacheSite.portfolio.id)
                formData.append('zip_code', zipCode)
            }
            const postUrl = getApiUrlSiteV2(api_url_shipping_cost_form_cost_calculation_site, reduxModalSite)
            axios({
                data: formData,
                headers: getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch),
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    if (response.data.error) {
                        setShipCostError(response.data.error)
                        dispatch(reduxSalesShippingCostSite(undefined, undefined))
                    } else {
                        const costIndex = shippingService ? response.data.costs.findIndex((val: any) => val.service_id === shippingService) : 0
                        if (shippingService) {
                            dispatch(reduxSalesShippingCostSite(response.data.costs?.[costIndex]?.cost, response.data.costs?.[costIndex]?.id))
                        } else {
                            dispatch(reduxSalesShippingCostSite(response.data.costs.filter((obj: any) => obj.agent !== 'Mondial Relay')?.[costIndex]?.cost, response.data.costs.filter((obj: any) => obj.agent !== 'Mondial Relay')?.[costIndex]?.id))
                        }
                        if (!hasMondialRelay) {
                            setOtherShippingCost(response.data.costs)
                        }
                    }
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'PortfolioCheckoutSite',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'getShippingCost',
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'PortfolioCheckoutSite',
                'getShippingCost',
            ))
        }
    }

    function handleTermsChange(checked: boolean) {
        setTermsAccepted(checked)
        if (checked) {
            setTermsError('')
        }
    }

    function handleCustomFieldChange(fieldName: string, value: any) {
        setCustomFields(prev => ({
            ...prev,
            [fieldName]: value
        }))
        // Clear error for this field when user starts typing
        if (customFieldErrors[fieldName]) {
            setCustomFieldErrors(prev => ({
                ...prev,
                [fieldName]: ''
            }))
        }
    }

    function validateCustomFields() {
        if (!currentPortfolioConfig) return { isValid: true, errors: {} }

        const fieldErrors: any = {}
        let hasErrors = false

        // Validate required fields
        for (const field of currentPortfolioConfig.contactFields) {
            if (field.required && !customFields[field.name]) {
                fieldErrors[field.name] = reduxText[7489] || 'This field is required'
                hasErrors = true
            }
        }

        // Validate phone number if required for portfolio 696
        if (reduxCacheSite.portfolio?.id === 696 && !contact_info.phone) {
            fieldErrors.phone = reduxText[7489] || 'This field is required'
            setErrors((prev: any) => ({
                ...prev,
                contact_info: {
                    ...prev.contact_info,
                    phone: reduxText[7489]
                }
            }))
            hasErrors = true
        }

        // Validate terms if required
        if (currentPortfolioConfig.termsRequired && !termsAccepted) {
            fieldErrors.terms_accepted = reduxText[10341] || 'You must accept the terms'
            setTermsError(reduxText[10341])
            hasErrors = true
        }

        // Update custom field errors state
        setCustomFieldErrors(fieldErrors)

        return {
            isValid: !hasErrors,
            errors: fieldErrors
        }
    }

    function getCustomFieldsData() {
        if (!currentPortfolioConfig) return {}

        const data: { [key: string]: any } = {}

        // Add custom contact fields
        currentPortfolioConfig.contactFields.forEach((field: any) => {
            if (customFields[field.name] !== undefined) {
                data[field.name] = customFields[field.name]
            }
        })

        // Add terms acceptance if required
        if (currentPortfolioConfig.termsRequired) {
            data.terms_accepted = termsAccepted
        }

        return data
    }

    const noShiping = !reduxCacheSite.portfolio?.is_shipping_activated || (reduxCacheSite.portfolio?.id === 696 && reduxSalesSite.weightTotal === 0) // TODO dynamic

    return (
        <div className={`portfolio-checkout ${deviceType}`}>
            <div className={`pc-wrap ${deviceType}`}>
                <div className={`pc-contact-info ${deviceType}`}>
                    <p className={`pc-title ${deviceType}`}>{reduxText[7474]}</p>
                    {reduxCacheSite.portfolio?.id === 696 && (
                        <>
                            <TextInputSite
                                error={errors.contact_info.first_name}
                                label={reduxText[7475]}
                                name='first_name'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.first_name || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.last_name}
                                label={reduxText[7476]}
                                name='last_name'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.last_name || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.email}
                                label={reduxText[7477]}
                                name='email'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.email || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.phone}
                                label='Téléphone'
                                name='phone'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.phone || ''}
                            />
                            {currentPortfolioConfig?.contactFields?.map((field: any) => (
                                field.type === 'text' ? (
                                    <TextInputSite
                                        key={field.name}
                                        error={customFieldErrors[field.name] || ''}
                                        label={field.label}
                                        name={field.name}
                                        onChange={(e) => handleCustomFieldChange(field.name, e.value)}
                                        stylesLabel={{ color: '#343434' }}
                                        value={customFields[field.name] || ''}
                                    />
                                ) : field.type === 'checkbox' ? (
                                    <div key={field.name}>
                                        <div
                                            className={`pc-checkbox-wrap ${deviceType}`}
                                            onClick={() => handleCustomFieldChange(field.name, !customFields[field.name])}
                                        >
                                            <input
                                                checked={customFields[field.name] || false}
                                                name={field.name}
                                                onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
                                                type='checkbox'
                                            />
                                            <p className={`pc-checkbox ${deviceType}`}>{field.label}</p>
                                        </div>
                                        {customFieldErrors[field.name] && (
                                            <span className='pc-form-error'>{customFieldErrors[field.name]}</span>
                                        )}
                                    </div>
                                ) : field.type === 'select' ? (
                                    <SelectListInputSite
                                        key={field.name}
                                        apiUrl={field.apiUrl || ''}
                                        error={customFieldErrors[field.name] || ''}
                                        label={field.label}
                                        name={field.name}
                                        onChange={(e) => handleCustomFieldChange(field.name, e)}
                                        stylesLabel={{ color: '#343434' }}
                                        value={customFields[field.name] || ''}
                                        options={field.options}
                                    />
                                ) : null
                            ))}
                        </>
                    )}
                    {reduxCacheSite.portfolio?.id !== 696 && (
                        <>
                            <TextInputSite
                                error={errors.contact_info.first_name}
                                label={reduxText[7475]}
                                name='first_name'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.first_name || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.last_name}
                                label={reduxText[7476]}
                                name='last_name'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.last_name || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.email}
                                label={reduxText[7477]}
                                name='email'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.email || ''}
                            />
                            <TextInputSite
                                error={errors.contact_info.phone}
                                label={reduxText[7478]}
                                name='phone'
                                onChange={(e) => handleInputChange(e, 'contact_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={contact_info.phone || ''}
                            />
                        </>
                    )}
                </div>
                <div className={`pc-shipping-info ${deviceType}`}>
                    <p className={`pc-title ${deviceType}`}>{(reduxCacheSite.portfolio?.stripe_capture_method_is_manual || noShiping) ? reduxText[7485] : reduxText[7479]}</p>
                    {hasMondialRelay && (
                        <div className={`pc-shipping-type ${deviceType}`}>
                            <div>
                                <input
                                    checked={shippingType === 'shipping'}
                                    name='payment_method'
                                    onChange={(e) => {
                                        setShippingType(e.target.value)
                                        setShipping_info({
                                            address: '',
                                            city: '',
                                            country: '',
                                            relay_point_id: '',
                                            relay_point_name: '',
                                            zip_code: '',
                                        })
                                        setErrors({
                                            ...errors,
                                            shipping_info: {},
                                        })
                                        setShipCostError('')
                                    }}
                                    type='radio'
                                    value='shipping'
                                />
                                <label>Livraison à domicile</label>
                            </div>
                            <div>
                                <input
                                    checked={shippingType === 'pickup'}
                                    name='payment_method'
                                    onChange={(e) => {
                                        setShippingType(e.target.value)
                                        setShipping_info({
                                            address: '',
                                            city: '',
                                            country: '',
                                            relay_point_id: '',
                                            relay_point_name: '',
                                            zip_code: '',
                                        })
                                        setErrors({
                                            ...errors,
                                            shipping_info: {},
                                        })
                                        setShipCostError('')
                                    }}
                                    type='radio'
                                    value='pickup'
                                />
                                <label>Livraison en point relais</label>
                            </div>
                        </div>
                    )}
                    {shippingType === 'shipping' && (
                        <>
                            <TextInputSite
                                error={errors.shipping_info.address}
                                label={reduxText[7480]}
                                name='address'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.address || ''}
                            />
                            <TextInputSite
                                error={errors.shipping_info.zip_code}
                                label={reduxText[7482]}
                                name='zip_code'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.zip_code || ''}
                            />
                            <TextInputSite
                                error={errors.shipping_info.city}
                                label={reduxText[7483]}
                                name='city'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.city || ''}
                            />
                            {/* TODO dynamic */}
                            <SelectListInputSite
                                apiUrl={getApiUrlSiteV2(!noShiping ? `${api_url_portfolio_site_select_country_shipping_country}?portfolio=${reduxCacheSite.portfolio?.id}${reduxCacheSite.portfolio?.id === 450 ? '&eshop_name=true' : ''}` : api_url_portfolio_site_select_country, reduxModalSite)}
                                error={errors.shipping_info.country}
                                label={reduxText[7484]}
                                stylesLabel={{ color: '#343434' }}
                                name='country'
                                onChange={(e: any) => handleInputChange(e, 'shipping_info')}
                                value={shipping_info.country || ''}
                            />
                        </>
                    )}
                    {shippingType === 'pickup' && (
                        <>
                            <div className={`pc-mondial-relay-widget ${deviceType}`}>
                                <MondialRelayWidgetSite
                                    getShippingCost={getShippingCost}
                                    shipping_info={shipping_info}
                                    setShipping_info={setShipping_info}
                                />
                            </div>
                            <TextInputSite
                                disabled
                                error={errors.shipping_info.relay_point_name}
                                label='Point relais'
                                name='relay_point_name'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.relay_point_name || ''}
                            />
                            <TextInputSite
                                disabled
                                error={errors.shipping_info.relay_point_id}
                                label='N° Point relais'
                                name='relay_point_id'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.relay_point_id || ''}
                            />
                            <TextInputSite
                                disabled
                                error={errors.shipping_info.address}
                                label={reduxText[7480]}
                                name='address'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.address || ''}
                            />
                            <TextInputSite
                                disabled
                                error={errors.shipping_info.zip_code}
                                label={reduxText[7482]}
                                name='zip_code'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.zip_code || ''}
                            />
                            <TextInputSite
                                disabled
                                error={errors.shipping_info.city}
                                label={reduxText[7483]}
                                name='city'
                                onChange={(e) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.city || ''}
                            />
                            {/* TODO dynamic */}
                            <SelectListInputSite
                                apiUrl={getApiUrlSiteV2(!noShiping ? `${api_url_portfolio_site_select_country_shipping_country}?portfolio=${reduxCacheSite.portfolio?.id}${reduxCacheSite.portfolio?.id === 450 ? '&eshop_name=true' : ''}` : api_url_portfolio_site_select_country, reduxModalSite)}
                                disabled
                                error={errors.shipping_info.country}
                                label={reduxText[7484]}
                                name='country'
                                onChange={(e: any) => handleInputChange(e, 'shipping_info')}
                                stylesLabel={{ color: '#343434' }}
                                value={shipping_info.country || ''}
                            />
                        </>
                    )}
                </div>
                {!noShiping && (
                    <div className={`pc-shipping-cost ${deviceType}`}>
                        {shipCostError ? (
                            <p className='pc-shipping-cost-text error'>{shipCostError}</p>
                        ) : (
                            <p className={`pc-shipping-cost-text ${deviceType}`}>{reduxText[8518]} {reduxSalesSite.shippingTotal && Number(reduxSalesSite.shippingTotal.toFixed(2))} {reduxSalesSite.shippingTotal !== undefined && reduxSalesSite.currency_symbol}</p>
                        )}

                        {otherShippingCost?.length! > 1 && (
                            <div className={`pc-other-costs ${deviceType}`}>
                                {otherShippingCost?.map(val => (
                                    <div
                                        key={val.id}
                                        className={`pc-other-costs-item${reduxSalesSite.shippingService === val.id ? ' active' : ''} ${deviceType}`}
                                        onClick={() => dispatch(reduxSalesShippingCostSite(val.cost, val.id))}
                                    >
                                        <p className={`pc-other-costs-item-service ${deviceType}`}>{val.agent} - {val.service}</p>
                                        <p className={`pc-other-costs-item-cost ${deviceType}`}>{Number(val.cost.toFixed(2))} {reduxSalesSite.currency_symbol}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {!noShiping && !reduxCacheSite.portfolio?.stripe_capture_method_is_manual && (
                    <div className={`pc-billing-info ${deviceType}`}>
                        <p className={`pc-title ${deviceType}`}>{reduxText[7485]}</p>
                        <div
                            className={`pc-checkbox-wrap ${deviceType}`}
                            onClick={() => handleInputChange({ name: 'same_as_shipping', value: !billing_info.same_as_shipping }, 'billing_info')}
                        >
                            <input
                                checked={billing_info.same_as_shipping || ''}
                                name='same_as_shipping'
                                onChange={(event) => handleInputChange({ name: event.target.name, value: event.target.checked }, 'billing_info')}
                                type='checkbox'
                            />
                            <p className={`pc-checkbox ${deviceType}`}>{reduxText[7486]}</p>
                        </div>
                        {!billing_info.same_as_shipping && (
                            <div>
                                <TextInputSite
                                    error={errors.billing_info.first_name}
                                    label={reduxText[7475]}
                                    stylesLabel={{ color: '#343434' }}
                                    name='first_name'
                                    onChange={(e) => handleInputChange(e, 'billing_info')}
                                    value={billing_info.first_name || ''}
                                />
                                <TextInputSite
                                    error={errors.billing_info.last_name}
                                    label={reduxText[7476]}
                                    stylesLabel={{ color: '#343434' }}
                                    name='last_name'
                                    onChange={(e) => handleInputChange(e, 'billing_info')}
                                    value={billing_info.last_name || ''}
                                />
                                <TextInputSite
                                    error={errors.billing_info.address}
                                    label={reduxText[7480]}
                                    stylesLabel={{ color: '#343434' }}
                                    name='address'
                                    onChange={(e) => handleInputChange(e, 'billing_info')}
                                    value={billing_info.address || ''}
                                />
                                <TextInputSite
                                    error={errors.billing_info.zip_code}
                                    label={reduxText[7482]}
                                    stylesLabel={{ color: '#343434' }}
                                    name='zip_code'
                                    onChange={(e) => handleInputChange(e, 'billing_info')}
                                    value={billing_info.zip_code || ''}
                                />
                                <TextInputSite
                                    error={errors.billing_info.city}
                                    label={reduxText[7483]}
                                    stylesLabel={{ color: '#343434' }}
                                    name='city'
                                    onChange={(e) => handleInputChange(e, 'billing_info')}
                                    value={billing_info.city || ''}
                                />
                                <SelectListInputSite
                                    apiUrl={getApiUrlSiteV2(api_url_portfolio_site_select_country, reduxModalSite)}
                                    error={errors.billing_info.country}
                                    label={reduxText[7484]}
                                    name='country'
                                    onChange={(e: any) => handleInputChange(e, 'billing_info')}
                                    stylesLabel={{ color: '#343434' }}
                                    value={billing_info.country || ''}
                                />
                            </div>
                        )}
                    </div>
                )}
                {currentPortfolioConfig?.termsRequired && (
                    <div className={`pc-terms-section ${deviceType}`}>
                        <div
                            className={`pc-checkbox-wrap ${deviceType}`}
                            onClick={() => handleTermsChange(!termsAccepted)}
                        >
                            <input
                                checked={termsAccepted}
                                name='terms_accepted'
                                onChange={(e) => handleTermsChange(e.target.checked)}
                                type='checkbox'
                                required
                            />
                            <p className={`pc-checkbox ${deviceType}`}>{currentPortfolioConfig.termsLabel}</p>
                        </div>
                        {termsError && (
                            <span className='pc-form-error'>{termsError}</span>
                        )}
                    </div>
                )}
                {hasError && (
                    <span className={`pc-form-error ${deviceType}`}>{reduxText[8517]}</span>
                )}
                {mainErrors?.shipping_cost && (
                    <span className='pc-form-error'>{mainErrors.shipping_cost}</span>
                )}
                {reduxCacheSite.portfolio?.token_paypal_client_id
                    ? (
                        <div onClick={() => validateCustomFields()}>
                            <PayPalScriptProvider
                                options={{
                                    'clientId': process.env.NODE_ENV === 'development' ? 'Ace5EvaBA0nxm5yVdRupa3VaRj3dwMyjgbYCZvhMzkhUJ-aqW-JdjKs_rs--e0yAhXTvwejrooQYtT5C' : reduxCacheSite.portfolio.token_paypal_client_id,
                                    'components': 'buttons',
                                    'currency': reduxCacheSite.portfolio.currency?.iso_code,
                                }}
                            >
                                <PaypalCheckoutSite
                                    billing_info={billing_info}
                                    contact_info={contact_info}
                                    customFieldsData={currentPortfolioConfig ? getCustomFieldsData() : {}}
                                    setErrors={setErrors}
                                    setHasError={setHasError}
                                    setMainErrors={setMainErrors}
                                    shipping_info={shipping_info}
                                    validateCustomFields={validateCustomFields}
                                />
                            </PayPalScriptProvider>
                        </div>
                    ) : (
                        <Elements stripe={reduxModalSite.stripePromise}>
                            <StripeCheckoutSite
                                billing_info={billing_info}
                                contact_info={contact_info}
                                customFieldsData={currentPortfolioConfig ? getCustomFieldsData() : {}}
                                setErrors={setErrors}
                                setHasError={setHasError}
                                setMainErrors={setMainErrors}
                                shippingHasError={Boolean(shipCostError)}
                                shipping_info={shipping_info}
                                validateCustomFields={validateCustomFields}
                            />
                        </Elements>
                    )}
            </div>
        </div>
    )
}
